import type { CollectionItem, CollectionItemCondition } from '@/types'
import { request } from './client'

export async function addToCollection(
  cardId: number,
  quantity: number = 1,
  acquiredPrice?: number,
  notes?: string,
  idempotencyKey?: string,
  condition?: string | null,
): Promise<CollectionItem> {
  const response = await request<{ collectionItem: CollectionItem }>('/collection', {
    method: 'POST',
    body: {
      cardId,
      quantity,
      acquiredPrice,
      notes,
      condition: (condition as CollectionItemCondition | null | undefined) ?? undefined,
    },
    idempotencyKey,
  })
  return response.collectionItem
}

export type CollectionItemUpdate = {
  quantity?: number
  acquiredPrice?: number | null
  notes?: string | null
  condition?: CollectionItemCondition | null
  /** Optimistic-concurrency hint. Echo the last-seen `updatedAt` from the
   * server; if another writer has touched the row since, the server returns
   * 409 which routes to `ConflictResolutionModal`. */
  expectedUpdatedAt?: string
}

export async function updateCollectionItem(
  id: number,
  updates: CollectionItemUpdate,
  idempotencyKey?: string,
): Promise<CollectionItem> {
  const response = await request<{ collectionItem: CollectionItem }>(`/collection/${id}`, {
    method: 'PATCH',
    body: updates,
    idempotencyKey,
  })
  return response.collectionItem
}

export async function removeFromCollection(id: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/collection/${id}`, { method: 'DELETE', idempotencyKey })
}

export async function removeFromCollectionByCardId(cardId: number, idempotencyKey?: string): Promise<void> {
  await request<void>(`/collection/card/${cardId}`, { method: 'DELETE', idempotencyKey })
}

export async function getCollectionItemByCard(cardId: number): Promise<CollectionItem | null> {
  try {
    const response = await request<{ collectionItem: CollectionItem }>(`/collection/card/${cardId}`)
    return response.collectionItem
  } catch (err) {
    // 404 means the user doesn't own the card — treat as "no item" rather than an error.
    if (err && typeof err === 'object' && 'status' in err && (err as { status?: number }).status === 404) {
      return null
    }
    throw err
  }
}

export async function updateCollectionByCard(
  cardId: number,
  updates: CollectionItemUpdate,
  idempotencyKey?: string,
): Promise<CollectionItem> {
  const response = await request<{ collectionItem: CollectionItem }>(`/collection/card/${cardId}`, {
    method: 'PATCH',
    body: updates,
    idempotencyKey,
  })
  return response.collectionItem
}

export async function getCollectionQuantitiesByBase(): Promise<{
  quantitiesByBase: Record<
    string,
    Array<{
      cardId: number
      quantity: number
      card: {
        id: number
        cardImageId: string
        name: string
        variantType: string
        rarity: string | null
        setName: string | null
        imageUrl: string | null
        marketPrice: number | null
      }
    }>
  >
}> {
  return request('/collection/quantities_by_base')
}
